const GLOBAL_LOGGERS = new Map();

export const TRACE = 1;
export const DEBUG = 2;
export const INFO =  3;
export const WARN =  4;
export const ERROR = 5;

/**
 * Set the level for all newly created loggers.
 */
export const setGlobalLevel = (level: number) => {
    for(let logger of GLOBAL_LOGGERS.values()) {
        logger.level = level;
    }
}

/**
 * This is the first point of interaction with the logger module. The suggested
 * usage is:
 *
 * ```
 * import logging from './logging';
 * const log = logging("name");
 * ```
 */
const getLogger = (name: string): Logger => {
    if(GLOBAL_LOGGERS.has(name)) {
        return GLOBAL_LOGGERS.get(name);
    } else {
        const logger = new Logger();
        GLOBAL_LOGGERS.set(name, logger);
        return logger;
    }
}
export default getLogger;

/**
 * The Logger class implements the trace, debug, info, warn, and error methods.
 * Each logger instance has an integer level property that can be set to one
 * of the constants (TRACE, DEBUG, etc.) on the Logger prototype.
 *
 * There's usually no need to directly instantiate a logger. The default
 * export, getLogger, gets a global, per-module, Logger instance.
 */
export class Logger {

    level: number;

    public TRACE = TRACE;
    public DEBUG = DEBUG
    public INFO =  INFO;
    public WARN =  WARN;
    public ERROR = ERROR;

    constructor() {
        this.level = ERROR;

        for(let method of ['trace', 'debug', 'info', 'warn', 'error', 'temp'])
        {
            this[method] = this[method].bind(this);
        }
    }

    prefix() {
        return (new Date()).toISOString();
    }

    trace(...args: unknown[]) {
        if(this.level <= this.TRACE)
            console.trace(this.prefix(), 'trace', ...args);
    }

    debug(...args: unknown[]) {
        // console.debug is suppressed by default in some browsers, so using
        // console.log instead is a simple way to take back control.
        if(this.level <= this.DEBUG)
            console.log(this.prefix(), 'debug', ...args);
    }

    info(...args: unknown[]) {
        if(this.level <= this.INFO)
            console.info(this.prefix(), 'info', ...args);
    }

    warn(...args: unknown[]) {
        if(this.level <= this.WARN)
            console.warn(this.prefix(), 'warn', ...args);
    }

    error(...args: unknown[]) {
        // Never drop errors.
        console.error(this.prefix(), 'error', ...args);
    }

    /**
     * Temp logs are show unconditionally under the presumption that they'll be
     * deleted before they're committed to production code.
     */
    temp(...args: unknown[]) {
        console.log(this.prefix(), 'TEMP', ...args);
    }
}

